import { useEffect, useState } from 'react'
import { UserList } from '../components/admin/UserList'
import { CreateUserModal } from '../components/admin/CreateUserModal'
import { useAdmin } from '../hooks/useAdmin'

export function Admin() {
  const { users, isLoading, error, refreshUsers, addUser, removeUser, resetPassword } = useAdmin()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    refreshUsers()
  }, [refreshUsers])

  const handleCreateUser = async (email: string, temporaryPassword?: string) => {
    await addUser({ email, temporaryPassword })
    setActionMessage({ type: 'success', text: `User ${email} created successfully` })
    setTimeout(() => setActionMessage(null), 5000)
  }

  const handleDeleteUser = async () => {
    if (deleteConfirm) {
      try {
        const result = await removeUser(deleteConfirm)
        setActionMessage({
          type: 'success',
          text: `User deleted. ${result.deletedFilesCount} file(s) removed.`,
        })
      } catch {
        setActionMessage({ type: 'error', text: 'Failed to delete user' })
      }
      setDeleteConfirm(null)
      setTimeout(() => setActionMessage(null), 5000)
    }
  }

  const handleResetPassword = async (username: string) => {
    try {
      await resetPassword(username)
      setActionMessage({ type: 'success', text: `Password reset email sent to ${username}` })
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to reset password' })
    }
    setTimeout(() => setActionMessage(null), 5000)
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Create and manage user accounts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Create User
        </button>
      </div>

      {actionMessage && (
        <div
          className={`mb-4 p-4 rounded-md ${
            actionMessage.type === 'success'
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}
        >
          <p
            className={`text-sm ${
              actionMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {actionMessage.text}
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <UserList
        users={users}
        isLoading={isLoading}
        onResetPassword={handleResetPassword}
        onDelete={(username) => setDeleteConfirm(username)}
      />

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateUser}
      />

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={() => setDeleteConfirm(null)}
            ></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete User</h3>
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to delete{' '}
                <span className="font-medium">{deleteConfirm}</span>?
              </p>
              <p className="text-sm text-red-600 mb-6">
                This will also permanently delete all files owned by this user.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
